'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

interface ComplianceEvent {
  id: string;
  title: string;
  description: string;
  due_date: string;
  status: string;
  event_type: string;
}

const complianceAreas = [
  { key: 'gst', label: 'GST', status: 'not_applicable', description: 'Not yet registered' },
  { key: 'roc', label: 'ROC / MCA', status: 'compliant', description: 'Annual filing on track' },
  { key: 'tds', label: 'TDS', status: 'due_soon', description: 'Q3 return due soon' },
  { key: 'income_tax', label: 'Income Tax', status: 'compliant', description: 'ITR filed for FY 2024-25' },
  { key: 'trademark', label: 'Trademark', status: 'not_applicable', description: 'Application in progress' },
  { key: 'msme', label: 'MSME', status: 'not_applicable', description: 'Not yet registered' },
];

const statusConfig: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  compliant: { label: 'Compliant', color: 'text-green-700', bg: 'bg-green-50', dot: 'bg-green-600' },
  due_soon: { label: 'Due soon', color: 'text-amber-800', bg: 'bg-amber-50', dot: 'bg-amber-600' },
  overdue: { label: 'Overdue', color: 'text-red-700', bg: 'bg-red-50', dot: 'bg-red-600' },
  not_applicable: { label: 'N/A', color: 'text-slate-600', bg: 'bg-slate-100', dot: 'bg-slate-400' },
};

export default function CompliancePage() {
  const [events, setEvents] = useState<ComplianceEvent[]>([]);
  const [healthScore, setHealthScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [aiExplaining, setAiExplaining] = useState<string | null>(null);
  const [aiExplanations, setAiExplanations] = useState<Record<string, string>>({});

  useEffect(() => {
    async function loadCompliance() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // Get business scoped to user (multi-tenant)
        const { data: business, error: bizErr } = await supabase
          .from('businesses')
          .select('id, compliance_profiles(health_score)')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (bizErr && bizErr.code !== 'PGRST116') throw bizErr;

        if (business) {
          setBusinessId(business.id);

          const complianceProfiles = Array.isArray(business.compliance_profiles)
            ? business.compliance_profiles
            : [];
          if (complianceProfiles[0]?.health_score) {
            setHealthScore(complianceProfiles[0].health_score);
          }

          // Scope compliance events to this business
          const { data, error: eventsErr } = await supabase
            .from('compliance_events')
            .select('*')
            .eq('business_id', business.id)
            .order('due_date', { ascending: true });

          if (eventsErr) throw eventsErr;
          setEvents(data || []);
        }
      } catch (err) {
        console.error('Compliance load error:', err);
        setError('Failed to load compliance data. Please refresh.');
      } finally {
        setLoading(false);
      }
    }
    loadCompliance();
  }, []);

  const getDaysUntil = (dateStr: string) => {
    return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
  };

  const filteredEvents = events.filter(e => {
    if (activeFilter === 'all') return true;
    return e.status === activeFilter;
  });

  const handleAiExplain = async (event: ComplianceEvent) => {
    if (aiExplanations[event.id]) return; // Already explained
    setAiExplaining(event.id);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'explain-compliance',
          item: {
            title: event.title,
            description: event.description,
            due_date: event.due_date,
            status: event.status,
            event_type: event.event_type,
          },
        }),
      });
      const data = await res.json();
      if (res.ok && data.explanation) {
        setAiExplanations(prev => ({ ...prev, [event.id]: data.explanation }));
      } else if (res.status === 503) {
        setAiExplanations(prev => ({ ...prev, [event.id]: 'AI not configured. Set OPENROUTER_API_KEY to activate AI explanations.' }));
      }
    } catch {
      setAiExplanations(prev => ({ ...prev, [event.id]: 'AI explanation unavailable. Please try again.' }));
    } finally {
      setAiExplaining(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/compliance" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-surface-3 rounded-xl w-64" />
              <div className="h-24 bg-white rounded-xl border border-border" />
              <div className="h-48 bg-white rounded-xl border border-border" />
              <div className="h-64 bg-white rounded-xl border border-border" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/compliance" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-6 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', border: '1px solid rgba(99,102,241,0.3)' }}
                  aria-hidden="true"
                >
                  <svg width={14} height={14} viewBox="0 0 18 18" fill="none">
                    <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
                    <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#818cf8" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-ink">Lexigen Compliance Center</h1>
              </div>
              <p className="text-[14px] text-ink-muted">AI-powered compliance intelligence for your business.</p>
            </div>
            {healthScore > 0 && (
              <div className="text-right flex-shrink-0">
                <div className="text-3xl font-bold text-ink">{healthScore}<span className="text-xl text-ink-muted">/100</span></div>
                <p className="text-[12px] font-semibold text-green-700">Compliance Health</p>
              </div>
            )}
          </div>

          {/* Error state */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" role="alert">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-[13px] font-semibold text-red-800">{error}</p>
            </div>
          )}

          {/* No business state */}
          {!businessId && !loading && (
            <div className="bg-white rounded-2xl border border-border p-10 text-center">
              <div className="w-14 h-14 bg-surface-3 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-ink mb-2">No business found</h2>
              <p className="text-[13px] text-ink-muted mb-6">Create your Business Passport to track compliance obligations.</p>
              <a href="/onboarding" className="inline-flex items-center gap-2 bg-ai text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-ai-dark transition-all text-[13px]">
                Create Business Passport →
              </a>
            </div>
          )}

          {businessId && (
            <>
              {/* AI Principle Banner */}
              <div
                className="rounded-2xl p-4 mb-6 flex items-center gap-4"
                style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.06), rgba(99,102,241,0.03))', border: '1px solid rgba(99,102,241,0.15)' }}
              >
                <div className="flex-1">
                  <p className="text-[11px] font-bold tracking-widest uppercase mb-0.5" style={{ color: '#6366f1' }}>LEXIGEN AI COMPLIANCE INTELLIGENCE</p>
                  <p className="text-[13px] text-ink-muted">AI monitors your obligations, explains what&apos;s due and why, and prepares what&apos;s needed. You approve and file.</p>
                </div>
                <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                  <div className="text-center px-3 py-2 rounded-xl" style={{ background: 'rgba(99,102,241,0.08)' }}>
                    <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#818cf8' }}>AI PREPARES</p>
                  </div>
                  <span className="text-ink-subtle" aria-hidden="true">·</span>
                  <div className="text-center px-3 py-2 rounded-xl" style={{ background: 'rgba(52,211,153,0.08)' }}>
                    <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#059669' }}>HUMANS APPROVE</p>
                  </div>
                </div>
              </div>

              {/* Health Score */}
              {healthScore > 0 && (
                <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-ink text-[15px]">Compliance Health</h2>
                    <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${
                      healthScore >= 80 ? 'text-green-700 bg-green-50' :
                      healthScore >= 50 ? 'text-amber-800 bg-amber-50' : 'text-red-700 bg-red-50'
                    }`}>
                      {healthScore >= 80 ? 'Healthy' : healthScore >= 50 ? 'Needs attention' : 'At risk'}
                    </span>
                  </div>
                  <div className="w-full bg-surface-3 rounded-full h-3 mb-4" role="progressbar" aria-valuenow={healthScore} aria-valuemin={0} aria-valuemax={100} aria-label={`Compliance health: ${healthScore}%`}>
                    <div
                      className={`h-3 rounded-full transition-all ${healthScore >= 80 ? 'bg-green-600' : healthScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${healthScore}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {complianceAreas.map(area => {
                      const config = statusConfig[area.status];
                      return (
                        <div key={area.key} className="p-3 bg-surface-2 rounded-xl">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} aria-hidden="true" />
                            <p className="text-[12px] font-semibold text-ink">{area.label}</p>
                          </div>
                          <p className="text-[11px] text-ink-muted">{area.description}</p>
                          <span className={`mt-2 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Compliance Calendar */}
              <div className="bg-white rounded-2xl border border-border p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                  <h2 className="font-semibold text-ink text-[15px]">Compliance Calendar</h2>
                  <div className="flex gap-2 flex-wrap">
                    {['all', 'due_soon', 'overdue', 'compliant'].map(filter => (
                      <button
                        key={filter}
                        onClick={() => setActiveFilter(filter)}
                        aria-pressed={activeFilter === filter}
                        className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                          activeFilter === filter ? 'text-white' : 'bg-surface-2 text-ink-muted hover:text-ink'
                        }`}
                        style={activeFilter === filter ? { background: 'linear-gradient(135deg, #4f46e5, #6366f1)' } : {}}
                      >
                        {filter === 'all' ? 'All' : filter === 'due_soon' ? 'Due soon' : filter === 'overdue' ? 'Overdue' : 'Compliant'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Empty state */}
                {filteredEvents.length === 0 && (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 bg-surface-3 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-[13px] font-semibold text-ink mb-1">
                      {activeFilter === 'all' ? 'No compliance events yet' : `No ${activeFilter.replace('_', ' ')} events`}
                    </p>
                    <p className="text-[12px] text-ink-muted">
                      {activeFilter === 'all' ?'Compliance events will appear here once your business is registered.' :'Try a different filter.'}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {filteredEvents.map(event => {
                    const days = getDaysUntil(event.due_date);
                    const config = statusConfig[event.status] || statusConfig.compliant;
                    const explanation = aiExplanations[event.id];

                    return (
                      <div key={event.id} className={`rounded-xl border transition-all ${
                        event.status === 'due_soon' ? 'border-amber-200 bg-amber-50/30' :
                        event.status === 'overdue'? 'border-red-200 bg-red-50/30' : 'border-border hover:bg-surface-2'
                      }`}>
                        <div className="flex items-start justify-between p-4 gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} aria-hidden="true" />
                            <div className="min-w-0">
                              <p className="font-semibold text-ink text-[14px]">{event.title}</p>
                              <p className="text-[12px] text-ink-muted mt-0.5">{event.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-[12px] font-medium text-ink">
                                {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                              </p>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                                {config.label}
                              </span>
                            </div>
                            {(event.status === 'due_soon' || event.status === 'overdue') && (
                              <button
                                onClick={() => handleAiExplain(event)}
                                disabled={aiExplaining === event.id}
                                aria-label={`Ask AI to explain ${event.title}`}
                                className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai whitespace-nowrap"
                              >
                                {aiExplaining === event.id ? 'Asking AI…' : 'Ask AI'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* AI Explanation */}
                        {explanation && (
                          <div className="mx-4 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-xl">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider">Lexigen AI</span>
                            </div>
                            <p className="text-[12px] text-indigo-900 leading-relaxed">{explanation}</p>
                            <p className="text-[10px] text-indigo-600 mt-2">Lexigen AI Analysis — verify with official sources. Not legal advice.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Link to AI assistant */}
              <div className="mt-6 p-4 bg-white rounded-xl border border-border flex items-center justify-between gap-4">
                <div>
                  <p className="text-[13px] font-semibold text-ink">Need help with compliance?</p>
                  <p className="text-[12px] text-ink-muted">Ask Lexigen AI about your specific obligations, deadlines, and what documents you need.</p>
                </div>
                <Link
                  href="/assistant"
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-ai text-white font-semibold px-4 py-2 rounded-xl hover:bg-ai-dark transition-all text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
                >
                  Ask AI →
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
